import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Mist } from "../components/temple/Mist";
import { Candles } from "../components/temple/Candles";
import { Reveal } from "../components/temple/Reveal";

const SOCIAL_LINKS = [
  { label: "X / Twitter", href: "https://x.com/LordKeku_", testId: "social-x", glyph: "𝕏" },
  { label: "Telegram Portal", href: "https://t.me/KekuPortal", testId: "social-tg-portal", glyph: "✦" },
  { label: "TikTok", href: "https://www.tiktok.com/@templeofkeku", testId: "social-tiktok", glyph: "♪" },
  { label: "Chart: Coming Soon", href: "#", testId: "social-chart", glyph: "𓂀", disabled: true },
  { label: "Contract: Coming Soon", href: "#", testId: "social-contract", glyph: "𓋹", disabled: true },
];

const ROADMAP = [
  {
    phase: "Phase I",
    title: "The Awakening",
    blurb: "The Temple opens.",
    body:
      "Building the foundation before anything else: the lore, the visuals, the community, the Telegram, the website, and the first Temple Watchers. Kek-u begins as a story before becoming something larger — the Father of All Pepes brought to life through ancient lore, cinematic content, memes, community raids, and the first believers who understand the origin.",
    focus: [
      "Build the Temple community",
      "Release Kek-u lore and visuals",
      "Grow X / Twitter organically",
      "Open Telegram portal and private group",
      "Launch the website with “Enter the Temple”",
      "Gather the first Temple Watchers",
    ],
    testId: "roadmap-phase-1",
  },
  {
    phase: "Phase II",
    title: "The Descent",
    blurb: "The Father of All Pepes enters the market.",
    body:
      "This is where Kek-u moves from myth into the open world. The community will already be watching, creating, posting, and spreading the story before launch. The focus is not hype for the sake of hype — it is keeping the lore alive, keeping the Temple active, and bringing more people into the origin story of Kek-u.",
    focus: [
      "Launch Kek-u",
      "Push the first major community raids",
      "Release more cinematic lore clips",
      "Grow the Temple Watchers",
      "Track and recognize real builders",
      "Expand memes, edits, and community content",
    ],
    testId: "roadmap-phase-2",
  },
  {
    phase: "Phase III",
    title: "The Temple Trials",
    blurb: "The community gets something to play.",
    body:
      "A simple Kek-u-themed puzzle/jewel game built around ancient relics, black-water crystals, frog symbols, purple shadow jewels, and hidden lore. Made for fun, community engagement, and keeping the Temple alive beyond regular posts. Players will unlock lore pieces, climb leaderboards, and take part in Temple challenges.",
    focus: [
      "Build a Kek-u puzzle / jewel game",
      "Add community leaderboard",
      "Add lore unlocks and temple relics",
      "Run Temple Trials and community challenges",
      "Recognize real contributors when possible",
      "Keep expanding the world of Kek-u",
    ],
    testId: "roadmap-phase-3",
  },
];

export default function TemplePage() {
  const navigate = useNavigate();
  const [entered, setEntered] = useState(false);

  useEffect(() => {
    // Fade page in after mount
    requestAnimationFrame(() => setEntered(true));
    window.scrollTo(0, 0);
  }, []);

  return (
    <div
      className={`relative min-h-screen w-full kek-stone-texture transition-opacity duration-700 ${
        entered ? "opacity-100" : "opacity-0"
      }`}
      data-testid="temple-page"
    >
      {/* Global mist drifting behind everything */}
      <div className="pointer-events-none fixed inset-0 z-0">
        <Mist intensity="soft" />
      </div>

      {/* ============ TOP WARNING STRIP ============ */}
      <div
        className="relative z-20 w-full"
        role="alert"
        data-testid="warning-strip-top"
        style={{
          background:
            "linear-gradient(90deg, rgba(20,8,32,0.92), rgba(42,14,74,0.6), rgba(20,8,32,0.92))",
          borderTop: "1px solid rgba(201,165,90,0.35)",
          borderBottom: "1px solid rgba(201,165,90,0.35)",
        }}
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-2.5 flex items-center justify-center gap-2.5 text-center">
          <span aria-hidden="true" className="kek-flicker text-[color:var(--kek-gold-bright)] text-sm sm:text-base">𓂀</span>
          <p className="font-cormorant text-[0.72rem] sm:text-sm text-[color:var(--kek-gold-bright)] tracking-wide leading-snug">
            <span className="font-cinzel uppercase tracking-[0.22em] text-[0.6rem] sm:text-[0.68rem] mr-2">Notice</span>
            Token not launched yet. The official contract will only be posted here and in the official Telegram / X when Kek-u awakens.
          </p>
          <span aria-hidden="true" className="kek-flicker text-[color:var(--kek-gold-bright)] text-sm sm:text-base">𓋹</span>
        </div>
      </div>

      {/* ============ HEADER ============ */}
      <header className="relative z-10 px-6 sm:px-10 pt-8 sm:pt-10">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <button
            onClick={() => navigate("/")}
            className="font-cinzel text-[0.7rem] sm:text-xs uppercase tracking-[0.4em] kek-gold-thin hover:text-[color:var(--kek-gold-bright)] transition-colors"
            data-testid="back-to-entry"
            aria-label="Return to entry"
          >
            ← Return
          </button>
          <p
            className="font-cinzel text-[0.7rem] sm:text-xs uppercase tracking-[0.4em] kek-gold-thin"
            data-testid="header-title"
          >
            ✦ The Temple of Kek-u ✦
          </p>
          <span className="w-12 sm:w-20" aria-hidden="true" />
        </div>
        <div className="kek-divider mt-6 sm:mt-8 max-w-6xl mx-auto" />
      </header>

      {/* ============ HERO / OPENING ============ */}
      <section className="relative z-10 px-6 sm:px-10 pt-16 sm:pt-24 pb-12">
        <div className="max-w-3xl mx-auto text-center">
          <Reveal testId="hero-glyph">
            <div className="mb-6 sm:mb-8 flex justify-center kek-flicker" aria-hidden="true">
              <EyeOfHorusGlyph />
            </div>
          </Reveal>
          <Reveal delay={120}>
            <h1
              className="font-cinzel kek-gold-text text-3xl sm:text-5xl lg:text-6xl font-bold tracking-[0.18em] uppercase"
              data-testid="temple-hero-title"
            >
              The Father Awakens
            </h1>
          </Reveal>
          <Reveal delay={240}>
            <p
              className="font-cormorant italic text-base sm:text-xl text-[color:var(--kek-text-muted)] mt-5 sm:mt-7"
              data-testid="temple-hero-sub"
            >
              You have stepped beyond the black waters. Tread softly — the
              lore listens.
            </p>
          </Reveal>
        </div>
      </section>

      {/* ============ LORE ============ */}
      <section
        id="lore"
        className="relative z-10 px-6 sm:px-10 py-20 sm:py-28"
        data-testid="lore-section"
      >
        <div className="max-w-4xl mx-auto">
          <Reveal>
            <SectionHeading title="The Lore" eyebrow="I · Origin" />
          </Reveal>

          <Reveal delay={120}>
            <div className="kek-card kek-corners p-7 sm:p-12 mt-10 sm:mt-14 relative">
              <div className="space-y-5 sm:space-y-6 font-cormorant text-lg sm:text-xl leading-relaxed text-[color:var(--kek-text)]/90">
                <p>
                  Lord Kek-u is inspired by ancient Egyptian mythology,
                  especially the{" "}
                  <span className="kek-gold-thin">Ogdoad of Hermopolis</span> —
                  the primordial forces connected to what existed before
                  creation: darkness, black waters, hidden forces, and the
                  world before light.
                </p>
                <p>
                  One of those ancient forces was{" "}
                  <span className="kek-gold-thin">Kek</span>, tied to
                  primordial darkness.
                </p>
                <p>
                  In our lore, Kek-u is the{" "}
                  <span className="kek-gold-thin">first frog</span> — the
                  ancient one before the meme, the father before the face,
                  the origin behind the Pepe bloodline.
                </p>
                <div className="kek-divider my-3" />
                <p className="font-cinzel text-base sm:text-lg uppercase tracking-[0.18em] text-[color:var(--kek-gold-bright)]">
                  Pepe was born on the internet.
                </p>
                <p className="font-cinzel text-base sm:text-lg uppercase tracking-[0.18em] text-[color:var(--kek-gold-bright)]">
                  Kek-u came before the internet.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ============ ROADMAP ============ */}
      <section
        id="roadmap"
        className="relative z-10 px-6 sm:px-10 py-20 sm:py-28"
        data-testid="roadmap-section"
      >
        <div className="max-w-6xl mx-auto">
          <Reveal>
            <SectionHeading title="Roadmap" eyebrow="II · The Path" centered />
          </Reveal>

          <div className="mt-12 sm:mt-16 grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
            {ROADMAP.map((p, i) => (
              <Reveal key={p.phase} delay={i * 120} testId={p.testId}>
                <RoadmapTablet phase={p} index={i + 1} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ============ WARNING (AFTER ROADMAP) ============ */}
      <section
        className="relative z-10 px-6 sm:px-10 py-14 sm:py-20"
        data-testid="warning-section"
      >
        <div className="max-w-3xl mx-auto">
          <Reveal>
            <div className="kek-card kek-corners p-8 sm:p-12 text-center relative">
              <div className="flex items-center justify-center gap-4 mb-5" aria-hidden="true">
                <span className="kek-flicker text-2xl sm:text-3xl text-[color:var(--kek-gold-bright)]">𓂀</span>
                <span className="font-cinzel text-[0.65rem] sm:text-xs uppercase tracking-[0.5em] kek-gold-thin">
                  Sacred Notice
                </span>
                <span className="kek-flicker text-2xl sm:text-3xl text-[color:var(--kek-gold-bright)]">𓋹</span>
              </div>
              <div className="kek-divider w-24 mx-auto mb-6" />
              <p className="font-cinzel kek-gold-text text-lg sm:text-2xl uppercase tracking-[0.16em] leading-relaxed">
                Token not launched yet.
              </p>
              <p className="font-cormorant text-base sm:text-xl text-[color:var(--kek-text)]/90 mt-5 leading-relaxed">
                The official contract will only be posted{" "}
                <span className="kek-gold-thin">here</span> and in the official{" "}
                <span className="kek-gold-thin">Telegram</span> /{" "}
                <span className="kek-gold-thin">X</span> when Kek-u awakens.
              </p>
              <p className="font-cormorant italic text-sm sm:text-base text-[color:var(--kek-text-muted)] mt-5">
                Beware of impostors. Trust only what emerges from the Temple itself.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ============ SOCIALS ============ */}
      <section
        id="socials"
        className="relative z-10 px-6 sm:px-10 py-20 sm:py-28"
        data-testid="socials-section"
      >
        <div className="max-w-5xl mx-auto">
          <Reveal>
            <SectionHeading title="Enter the Temple" eyebrow="III · The Gates" centered />
          </Reveal>

          <p className="font-cormorant italic text-center text-[color:var(--kek-text-muted)] text-base sm:text-lg mt-5 sm:mt-6 max-w-xl mx-auto">
            Pass through the gates and join the Temple Watchers.
          </p>

          <div className="mt-12 sm:mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
            {SOCIAL_LINKS.map((s, i) => (
              <Reveal key={s.label} delay={i * 80}>
                <SocialGate {...s} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ============ FOOTER ============ */}
      <footer
        className="relative z-10 mt-10 pb-16"
        data-testid="footer"
      >
        <div className="relative max-w-5xl mx-auto px-6 sm:px-10">
          <div className="kek-divider mb-10" />
          <div className="relative h-28 sm:h-36">
            <Candles />
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
              <p
                className="font-cinzel kek-gold-text text-base sm:text-2xl uppercase tracking-[0.25em]"
                data-testid="footer-text"
              >
                The Father of All Pepes has awakened.
              </p>
              <p className="font-cormorant italic text-xs sm:text-sm text-[color:var(--kek-text-muted)] mt-4">
                © Temple of Kek-u — Lore-first project. Not financial advice.
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

/* ============ Small internal components ============ */

const SectionHeading = ({ title, eyebrow, centered = false }) => (
  <div className={centered ? "text-center" : ""}>
    {eyebrow && (
      <p className="font-cinzel kek-gold-thin text-[0.65rem] sm:text-xs uppercase tracking-[0.5em] mb-3">
        {eyebrow}
      </p>
    )}
    <h2 className="kek-section-title text-3xl sm:text-5xl">{title}</h2>
    <div
      className={`kek-divider mt-5 sm:mt-6 w-32 sm:w-40 ${
        centered ? "mx-auto" : ""
      }`}
    />
  </div>
);

const RoadmapTablet = ({ phase, index }) => (
  <article className="kek-card kek-corners h-full p-6 sm:p-8 flex flex-col">
    <div className="flex items-center justify-between mb-5">
      <span className="font-cinzel text-[0.6rem] sm:text-xs uppercase tracking-[0.4em] kek-gold-thin">
        {phase.phase}
      </span>
      <span
        className="font-cinzel text-[0.6rem] sm:text-xs kek-gold-thin"
        aria-hidden="true"
      >
        {"𓂀 ".repeat(1)}
        {String(index).padStart(2, "0")}
      </span>
    </div>
    <h3 className="font-cinzel kek-gold-text text-xl sm:text-2xl uppercase tracking-[0.14em]">
      {phase.title}
    </h3>
    <p className="font-cormorant italic text-[color:var(--kek-gold)] mt-2 text-base sm:text-lg">
      {phase.blurb}
    </p>
    <div className="kek-divider my-5" />
    <p className="font-cormorant text-[color:var(--kek-text)]/85 text-base sm:text-[1.05rem] leading-relaxed">
      {phase.body}
    </p>
    <div className="mt-6">
      <p className="font-cinzel text-[0.65rem] sm:text-xs uppercase tracking-[0.4em] kek-gold-thin mb-3">
        Focus
      </p>
      <ul className="space-y-2">
        {phase.focus.map((f) => (
          <li
            key={f}
            className="font-cormorant text-[color:var(--kek-text)]/85 text-base flex items-start"
          >
            <span className="kek-gold-thin mr-2 mt-0.5" aria-hidden="true">
              ✦
            </span>
            <span>{f}</span>
          </li>
        ))}
      </ul>
    </div>
  </article>
);

const SocialGate = ({ label, href, testId, glyph, disabled = false }) => {
  const inner = (
    <div
      className={`kek-card kek-corners group relative p-6 sm:p-7 flex items-center gap-5 transition-transform duration-300 ${
        disabled
          ? "opacity-70 cursor-not-allowed"
          : "hover:-translate-y-0.5"
      }`}
    >
      <div
        className="shrink-0 w-12 h-12 sm:w-14 sm:h-14 rounded-sm flex items-center justify-center font-cinzel text-xl sm:text-2xl"
        style={{
          background:
            "linear-gradient(180deg, rgba(20,8,32,0.9), rgba(8,3,16,0.95))",
          border: "1px solid rgba(201,165,90,0.45)",
          color: "var(--kek-gold-bright)",
          boxShadow: "inset 0 0 18px rgba(138,77,214,0.25)",
        }}
        aria-hidden="true"
      >
        {glyph}
      </div>
      <div className="min-w-0">
        <p className="font-cinzel text-sm sm:text-base uppercase tracking-[0.22em] text-[color:var(--kek-gold-bright)] truncate">
          {label}
        </p>
        <p className="font-cormorant italic text-xs sm:text-sm text-[color:var(--kek-text-muted)] mt-1">
          {disabled ? "Sealed for now" : "Pass through →"}
        </p>
      </div>
    </div>
  );
  if (disabled) {
    return (
      <div data-testid={testId} aria-disabled="true">
        {inner}
      </div>
    );
  }
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={testId}
      className="block focus:outline-none"
    >
      {inner}
    </a>
  );
};

const EyeOfHorusGlyph = () => (
  <svg
    width="84"
    height="84"
    viewBox="0 0 100 100"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <defs>
      <radialGradient id="gold" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stopColor="#f3d68a" />
        <stop offset="100%" stopColor="#8a6a2c" />
      </radialGradient>
    </defs>
    <circle cx="50" cy="50" r="46" stroke="url(#gold)" strokeWidth="1" opacity="0.4" />
    <path
      d="M10 52 C 25 35, 45 30, 60 38 C 72 44, 82 50, 90 52 C 82 56, 72 62, 60 66 C 45 72, 25 70, 10 52 Z"
      stroke="url(#gold)"
      strokeWidth="1.5"
      fill="none"
    />
    <circle cx="55" cy="52" r="7" fill="url(#gold)" />
    <circle cx="55" cy="52" r="3" fill="#06030a" />
    <path d="M55 67 L 52 80" stroke="url(#gold)" strokeWidth="1.5" />
    <path d="M65 64 C 70 72, 78 75, 82 78" stroke="url(#gold)" strokeWidth="1.5" fill="none" />
  </svg>
);
